import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { FileOutlined } from '@ant-design/icons';

import { AppButton, AppCard, AppSpin, AppTable } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useVisitCheckResultQuery } from '@/hooks/main/useVisitCheckResultQuery';
import { useVisitCheckStatusQuery } from '@/hooks/main/useVisitCheckStatusQuery';
import type { VisitCheckRow } from '@/types/visitCheck.types';
import { convGtJDate } from '@/utils/dateConverter';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { MASSAGER_LABEL_A2, MASSAGER_LABEL_R2, SERVICE_TYPE_R2 } from '@/constants/serviceType';

/**
 * 訪問データチェック result page.
 *
 * Mounted at /:tenant/rezepts/visit_check_ready/:ids/:yearmonth/:visitmonth (per BD).
 *
 * The token (cache key) is NOT in the URL. Two channels deliver it from the
 * progress dialog → here:
 *   1. React Router `location.state.token` — primary, fastest path.
 *   2. `sessionStorage['visitCheckToken']` — fallback that survives F5 in the
 *      same tab.
 * The BE validates the token against the authenticated user's clinic_id
 * (returns 403 / 404 if mismatched / expired).
 */
const VisitCheckResultPage: React.FC = () => {
  const { yearmonth } = useParams<{ ids: string; yearmonth: string; visitmonth: string }>();
  const location = useLocation();
  const { tenant } = useClinicContext();
  const { serviceType } = useTheme();
  const isR2 = serviceType === SERVICE_TYPE_R2;
  const massagerNameColumnTitle = `${isR2 ? MASSAGER_LABEL_R2 : MASSAGER_LABEL_A2}名`;
  const stateToken = (location.state as { token?: string } | null)?.token ?? null;
  const token = stateToken ?? sessionStorage.getItem('visitCheckToken');
  const {
    data: statusData,
    isError: isStatusError,
    error: statusError,
  } = useVisitCheckStatusQuery(token, !!token);
  const { data, isLoading, isError, error } = useVisitCheckResultQuery(
    token,
    !!token && statusData?.done === true
  );
  const [dots, setDots] = useState('');

  useEffect(() => {
    if (!token || statusData?.done) {
      setDots('');
      return undefined;
    }
    const id = window.setInterval(() => {
      setDots(prev => (prev.length >= 6 ? '' : prev + '.'));
    }, 1000);
    return () => window.clearInterval(id);
  }, [token, statusData?.done]);

  // BD §8: clicking the file icon opens visit schedule for that massager + date in a new tab.
  const openVisitSchedule = useCallback(
    (massagerId: number, treatmentDate: string) => {
      window.open(
        `/${tenant}/visits/schedule/${massagerId}/${treatmentDate}`,
        '_blank',
        'noopener'
      );
    },
    [tenant]
  );

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const searchYearmonth = data?.search_yearmonth || yearmonth || '';

  const columns: ColumnsType<VisitCheckRow> = useMemo(
    () => [
      {
        title: '受診者番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 130,
        render: (serial: string, row: VisitCheckRow) => (
          <PatientEditLink
            patientId={row.patient_id}
            target="_blank"
            value={serial}
          />
        ),
      },
      { title: '受診者名', dataIndex: 'patient_name', key: 'patient_name', width: 200 },
      {
        title: massagerNameColumnTitle,
        dataIndex: 'massager_name',
        key: 'massager_name',
        width: 150,
      },
      {
        title: '施術日',
        dataIndex: 'treatment_date',
        key: 'treatment_date',
        width: 200,
        render: (date: string, row: VisitCheckRow) => (
          <span className="inline-flex items-center gap-1">
            <AppButton
              size="small"
              icon={<FileOutlined />}
              onClick={() => openVisitSchedule(row.massager_id, date)}
              aria-label="訪問編集"
            />
            <span>{convGtJDate(date)}</span>
          </span>
        ),
      },
      { title: '対応ＮＯ', dataIndex: 'number_marker', key: 'number_marker', width: 110 },
      { title: '確認内容', dataIndex: 'error_message', key: 'error_message', width: 485 },
    ],
    [massagerNameColumnTitle, openVisitSchedule]
  );

  if (!token) {
    return (
      <div className="p-4">
        <AppCard>
          <p className="text-red-600">結果取得に必要なトークンが見つかりません。</p>
          <AppButton className="mt-3" onClick={() => window.close()}>
            閉じる
          </AppButton>
        </AppCard>
      </div>
    );
  }

  if (!statusData?.done) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <AppSpin />
          {isStatusError ? (
            <span className="text-red-600">
              {statusError instanceof Error
                ? statusError.message
                : '処理状況の取得に失敗しました。'}
            </span>
          ) : (
            <>
              <span className="text-base">
                訪問データチェック中<span>{dots}</span>
              </span>
              <span className="text-sm text-gray-600">
                チェック済み：{statusData?.processed ?? 0}/{statusData?.total ?? 0} 件処理中
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <AppSpin />
          <span className="text-base">表示準備中</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4">
        <AppCard>
          <p className="text-red-600">
            {error instanceof Error ? error.message : '結果の取得に失敗しました。'}
          </p>
          <AppButton className="mt-3" onClick={() => window.close()}>
            閉じる
          </AppButton>
        </AppCard>
      </div>
    );
  }

  return (
    <div className="p-4 pb-20 visit-check-result-page">
      <AppCard className="mb-3">
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold">提出年月：{formatYearmonthJp(searchYearmonth)}</div>
          {rows.length > 0 && <div className="text-base font-semibold">{total}件</div>}
        </div>
      </AppCard>

      {rows.length > 0 ? (
        <AppTable<VisitCheckRow>
          className="visit-check-result-table"
          rowKey={(row, idx) =>
            `${row.patient_id}-${row.massager_id}-${row.treatment_date}-${row.number_marker}-${idx ?? 0}`
          }
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 1100 }}
        />
      ) : (
        <div className="py-10 text-center text-gray-600">確認対象となる訪問はありません。</div>
      )}

      <div className="m-4 text-sm leading-relaxed">
        <p className="mb-1">※ 以下の方法で修正してください。</p>
        <p>① 訪問入力画面の詳細ボタン→距離編集ボタンで確認してください。</p>
        <p>
          ②
          該当の訪問がロック（グレー色）されている場合は、レセプトデータを未印刷に戻し、訪問入力で「当月再更新」にチェックしてデータ更新をしてください。
        </p>
        <p>③ 訪問入力画面の患者名（赤色）をクリックし、エラー内容を確認してください。</p>
        <p>
          ④
          訪問入力画面で「当月再更新」にチェックしてデータ更新をしてください。更新されない場合は、該当の訪問を削除し登録し直してください。
        </p>
        <p>
          ⑤
          メンテナンスが必要の可能性があります。エムワンデータラボ・カスタマーサポート（03-3258-0141）までお問い合わせください。
        </p>
      </div>
    </div>
  );
};

/** YYYYMM → "令和XX年MM月" — zero-padded YY + MM to match legacy convYmJDate output. */
function formatYearmonthJp(yyyymm: string): string {
  if (!/^\d{6}$/.test(yyyymm)) return yyyymm;
  const yyyy = Number(yyyymm.slice(0, 4));
  const mm = yyyymm.slice(4, 6);
  // Reiwa starts 2019-05-01.
  if (yyyy >= 2019) {
    const reiwa = String(yyyy - 2018).padStart(2, '0');
    return `令和${reiwa}年${mm}月`;
  }
  return `${yyyy}年${mm}月`;
}

export default VisitCheckResultPage;
